import { InputProps, OutlinedInput, Stack, styled } from '@mui/material';
import { QueryBuilderDnD } from '@react-querybuilder/dnd';
import {
  defaultMuiComponents,
  MaterialActionProps,
  QueryBuilderMaterial,
} from '@react-querybuilder/material';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { QueryBuilder } from 'react-querybuilder';
import 'react-querybuilder/dist/query-builder.css';
import { useSelectedPartner } from 'src/graphql';
import {
  CustomViewFormConditionField,
  CustomViewFormModel,
} from 'src/graphql/entities/custom-view';
import { getOperatorsByUiType } from 'src/graphql/entities/custom-view/custom-view-config/operators';
import {
  mapValuesToOptions,
  resolveFieldOptions,
  useCustomViewFilterOptions,
} from 'src/graphql/entities/custom-view/custom-view-config/use-custom-view-options';
import { getRuleFieldLabelSpaceStyles } from 'src/pages/Settings/rules/rule-field-label-space';
import { conditionsCustomControls } from 'src/pages/Settings/rules/rules-form-config';
import { CustomViewOperatorSelector } from './custom-view-operator-selector';

const CONTROL_ELEMENTS = {
  ...conditionsCustomControls,
  addGroupAction: () => null,
  operatorSelector: CustomViewOperatorSelector,
};

const muiComponents = {
  ...defaultMuiComponents,
  Input: (props: InputProps) => <OutlinedInput size="small" {...props} />,
} as MaterialActionProps['muiComponents'];

export const RulesFormContainer = styled(Stack, {
  name: 'RulesFormContainer',
})(({ theme }) => ({
  '& .ruleGroup': {
    flexDirection: 'column-reverse',
    border: 'none',
    background: 'none',
  },

  '& .ruleGroup-body': {
    '& .betweenRules, & .betweenRules .MuiInputBase-formControl': {
      minWidth: '5rem',
      maxWidth: '20% !important',
    },

    // Fields share the row and shrink with it, so a rule fits the dialog without a sideways scroll.
    '.MuiStack-root': {
      maxWidth: '16rem',
      flex: '1 1 0',
      minWidth: 0,
    },

    '.MuiStack-root .MuiFormControl-root': {
      maxWidth: '16rem',

      flex: '1 1 0',
      minWidth: 0,
    },

    '& .rule-operators': {
      flex: '0 1 10rem',
      minWidth: 0,
      maxWidth: '30% !important',
    },

    '& .rule-value': {
      flex: '1 1 auto',
      minWidth: 0,
    },

    display: 'grid !important',
    alignItems: 'start',
    alignSelf: 'stretch',
    gridTemplateColumns: 'auto minmax(0, 1fr)',

    '& > .rule': {
      minWidth: 0,
    },

    // The shared rule controls keep a 12rem floor that other rule screens have room for.
    '& .rule .MuiAutocomplete-root': {
      minWidth: 0,
    },

    '& .rule > .MuiAutocomplete-root, & .rule-value .MuiAutocomplete-root': {
      flex: '1 1 0',
    },

    '& .queryBuilder-dragHandle': {
      display: 'flex',
      alignItems: 'center',
    },
  },

  // Rules always take the wide column, a lone rule included; the AND/OR switch sits beside them.
  '& .ruleGroup-body > .rule, & .ruleGroup-body > .ruleGroup': {
    gridColumn: 2,
  },

  '& .ruleGroup-body > .betweenRules': {
    gridColumn: 1,
  },

  ...getRuleFieldLabelSpaceStyles(theme, { combinatorBesideRule: true }),
}));

export const CustomViewsQueryBuilder = ({
  name,
  onChange,
  filterAttributesList,
}: {
  name: string;
  filterAttributesList: CustomViewFormConditionField[];
  onChange?: (values: CustomViewFormModel['conditions']) => void;
}) => {
  const { control } = useFormContext();
  const { selectedPartner } = useSelectedPartner();

  const query = useWatch({
    control,
    name,
  }) as CustomViewFormModel['conditions'];

  useEffect(() => {
    if (query.rules?.length) {
      onChange?.(query);
    }
  }, [query, onChange]);

  const queryOptions = useCustomViewFilterOptions({
    partnerId: selectedPartner?.partnerId,
    conditions: query.rules,
  });

  const mappedFields = useMemo(() => {
    return {
      ...query,
      rules: query.rules.map((rule) => {
        if (!Array.isArray(rule.value)) {
          return rule;
        }

        const anyOptions = resolveFieldOptions(rule.field, queryOptions);

        if (!anyOptions.length) {
          return rule;
        }

        return {
          ...rule,
          value: mapValuesToOptions(rule.value, anyOptions),
        };
      }),
    };
  }, [query, queryOptions]);

  return (
    <RulesFormContainer>
      <QueryBuilderDnD>
        <QueryBuilderMaterial muiComponents={muiComponents}>
          <Controller
            control={control}
            name={name}
            rules={{ required: true }}
            render={({ field }) => (
              <QueryBuilder
                // @ts-expect-error query builder expects a string, but actually fields are object
                getDefaultField={(items) => items[0]}
                enableDragAndDrop
                enableMountQueryChange={false}
                resetOnFieldChange
                resetOnOperatorChange
                context={{
                  timezone: selectedPartner?.timeZone ?? 'America/New_York',
                  queryOptions,
                }}
                autoSelectField
                showCombinatorsBetweenRules
                query={mappedFields}
                onQueryChange={field.onChange}
                fields={filterAttributesList ?? []}
                // @ts-expect-error Overriding default library typings because of custom fields structure.
                getOperators={(f: CustomViewFormConditionField) =>
                  getOperatorsByUiType(f?.uiType)
                }
                // @ts-expect-error Overriding default library typings because of custom fields structure.
                controlElements={CONTROL_ELEMENTS}
              />
            )}
          />
        </QueryBuilderMaterial>
      </QueryBuilderDnD>
    </RulesFormContainer>
  );
};
